import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {ActivationChannel} from '../../domain/entities/Activation.ts'
import {TeamMember} from '../../domain/entities/Pipeline.ts'
import {
    ConversionCommand,
    ConversionLostReason,
    ConversionOutcome,
    ConversionStage,
    CrmConversionDetail,
    EXTRA_PAGES_DECISIONS,
    ExtraPagesDecision,
    isFinalConversionStage,
} from '../../domain/entities/ConversionPipeline.ts'
import {useCrmConversion, useCrmMutations} from '../../hooks/usePipelines.ts'
import {crmErrorMessage, dayCounter, formatMoney, memberById, newCommandId, SECTION_LABEL, TONE, travelerName, useDateFormatters} from './pipelineView.ts'
import {Avatar, ConversionBadges, CopyButton, Countries, MarketChip, Spinner, Tag} from './PipelineParts.tsx'
import {ContactSection, HistorySection, NextTouchSection, NotesSection} from './DetailSections.tsx'
import {AddFollowUpDialog, MarkDoneDialog} from './PipelineDialogs.tsx'
import {DraftSentDialog, HoldDialog, PendingReasonDialog, SentToPrintDialog, ValueDialog} from './ConversionDialogs.tsx'
import {DetailPanelShell} from './DetailPanelShell.tsx'
import {tripBookEditorUrl} from '../../utils/webAppUrl.ts'

const SECTION = 'px-6 py-5 border-b border-neutralBlack-100'
const GHOST_BTN = 'h-8 px-3 rounded-lg border border-neutralBlack-200 bg-white text-[12.5px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out disabled:cursor-not-allowed disabled:text-neutralBlack-500'
const PRIMARY_BTN = 'h-9 px-4 rounded-[10px] bg-neutralBlack-900 text-white text-[13px] font-semibold hover:bg-neutralBlack-700 transition-colors duration-200 ease-out disabled:bg-neutralBlack-100 disabled:text-neutralBlack-600 disabled:cursor-not-allowed'
const FOOTER_BTN = 'h-9 px-3.5 rounded-[10px] border border-neutralBlack-200 bg-white text-[13px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out'
const LINK_BTN = 'h-7 px-2.5 rounded-lg border border-neutralBlack-200 inline-flex items-center text-[12.5px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out'

const DRAFT_SENT_STAGES: ConversionStage[] = ['DRAFT_READY', 'CHANGES_ADAPTATION_READY']
const PRINT_STAGES: ConversionStage[] = ['READY_TO_PRINT', 'SENT_TO_PRINT_SHOP', 'PRINTING_SHIPPING', 'DELIVERED']

type ConversionDetailPanelProps = {
    conversionId: string
    team: TeamMember[]
    currentUserId: string | null
    onClose: () => void
    // The stage drop-down sends SET_STAGE through the same path as a drop.
    onRequestMove: (conversionId: string, stage: ConversionStage, currentStage: ConversionStage) => void
    onRequestOverride: (conversionId: string, currentStage: ConversionStage) => void
    onRequestLost: (conversionId: string, reason: ConversionLostReason | null, allowedReasons?: ConversionLostReason[]) => void
}

type SendCommand = (command: ConversionCommand, successKey: string) => Promise<unknown>

const Row: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
    <div className="flex items-center gap-2.5 min-h-[32px]">
        <span className="w-[120px] flex-none text-[12.5px] text-neutralBlack-600">{label}</span>
        <span className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap text-[13px] text-neutralBlack-900">{children}</span>
    </div>
)

const ExternalLink: React.FC<{href: string; children: React.ReactNode}> = ({href, children}) => {
    const {t} = useTranslation('dashboard')
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_BTN}>
            {children}<span className="sr-only"> {t('pipelines.newTab')}</span>
        </a>
    )
}

// ── TripBook (production) ────────────────────────────────────────────────

const TripBookSection: React.FC<{
    detail: CrmConversionDetail
    team: TeamMember[]
    sendCommand: SendCommand
    onSetPages: () => void
    onSetFlipbook: () => void
}> = ({detail, team, sendCommand, onSetPages, onSetFlipbook}) => {
    const {t} = useTranslation('dashboard')
    const {formatDateTime} = useDateFormatters()
    const {conversion, tripBook} = detail
    const allowed = (type: ConversionCommand['type']) => detail.allowedCommands?.includes(type)
    const editorId = tripBook?.editorId ?? conversion.editorId
    const editor = memberById(team, editorId)
    const editorName = tripBook?.editorName ?? editor?.name ?? editorId
    const flipbookUrl = tripBook?.flipbookUrl ?? detail.shortcuts?.flipbookUrl ?? null
    const pages = tripBook?.pagesUploaded ?? conversion.pages.uploaded
    const included = tripBook?.includedPages ?? conversion.pages.included
    const agencyReview = conversion.flags.agencyReview

    return (
        <section className={SECTION} aria-labelledby="pl-tb-title">
            <h3 id="pl-tb-title" className={`${SECTION_LABEL} mb-3`}>{t('pipelines.conversion.tripBook')}</h3>
            <Row label={t('pipelines.conversion.mode')}>
                {(tripBook?.mode ?? conversion.mode) ? t(`pipelines.mode.${tripBook?.mode ?? conversion.mode}`) : '—'}
            </Row>
            <Row label={t('pipelines.conversion.editor')}>
                {allowed('ASSIGN_EDITOR') && team.length > 0 ? (
                    <label className="inline-flex items-center">
                        <span className="sr-only">{t('pipelines.conversion.editor')}</span>
                        <select value={editorId ?? ''}
                                onChange={(e) => sendCommand({commandId: newCommandId(), type: 'ASSIGN_EDITOR', data: {editorId: e.target.value || null}},
                                    'pipelines.feedback.editorAssigned').catch(() => undefined)}
                                className="h-8 pl-2 pr-7 rounded-lg border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-900">
                            <option value="">{t('pipelines.unassigned')}</option>
                            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </label>
                ) : editorId ? (
                    <><Avatar member={editor} name={editorName} size={20}/><span>{editorName}</span></>
                ) : <span className="text-neutralBlack-600">{t('pipelines.unassigned')}</span>}
            </Row>
            <Row label={t('pipelines.conversion.pages')}>
                <span>{pages == null ? '—' : included != null ? t('pipelines.pages.ofIncluded', {pages, included}) : t('pipelines.pages.count', {count: pages})}</span>
                {allowed('SET_PAGES') && (
                    <button type="button" onClick={onSetPages} className="h-7 px-2 rounded-md text-[12px] font-medium text-neutralBlack-700 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                        {t('pipelines.edit')}<span className="sr-only"> {t('pipelines.conversion.pages')}</span>
                    </button>
                )}
            </Row>
            {tripBook && (tripBook.draftsSent ?? 0) > 0 && (
                <Row label={t('pipelines.conversion.drafts')}>
                    {t('pipelines.conversion.draftsSent', {count: tripBook.draftsSent ?? 0, date: formatDateTime(tripBook.draftSentAt)})}
                </Row>
            )}
            {tripBook?.draftApprovedAt && <Row label={t('pipelines.conversion.approved')}>{formatDateTime(tripBook.draftApprovedAt)}</Row>}
            <Row label={t('pipelines.conversion.flipbook')}>
                {flipbookUrl ? (
                    <>
                        <ExternalLink href={flipbookUrl}>{t('pipelines.conversion.openFlipbook')}</ExternalLink>
                        <CopyButton text={flipbookUrl} label={t('pipelines.conversion.copyFlipbook')}/>
                    </>
                ) : <span className="text-neutralBlack-600">—</span>}
                {allowed('SET_FLIPBOOK') && (
                    <button type="button" onClick={onSetFlipbook} className="h-7 px-2 rounded-md text-[12px] font-medium text-neutralBlack-700 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                        {flipbookUrl ? t('pipelines.edit') : t('pipelines.conversion.setFlipbook')}
                    </button>
                )}
            </Row>
            <Row label={t('pipelines.badge.agencyReview')}>
                {allowed('SET_AGENCY_REVIEW') ? (
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={agencyReview} className="accent-neutralBlack-900"
                               onChange={(e) => sendCommand({commandId: newCommandId(), type: 'SET_AGENCY_REVIEW', data: {value: e.target.checked}},
                                   'pipelines.feedback.agencyReview').catch(() => undefined)}/>
                        {t('pipelines.conversion.agencyReviewLabel')}
                    </label>
                ) : agencyReview ? t('pipelines.yes') : t('pipelines.no')}
            </Row>
        </section>
    )
}

// ── Extra pages and payment ──────────────────────────────────────────────

const ExtraPagesSection: React.FC<{detail: CrmConversionDetail; sendCommand: SendCommand}> = ({detail, sendCommand}) => {
    const {t, i18n} = useTranslation('dashboard')
    const {formatDateTime} = useDateFormatters()
    const [busy, setBusy] = useState(false)
    const [payError, setPayError] = useState<string | null>(null)
    useEffect(() => setPayError(null), [detail.conversion.id])
    const {extraPages, payment, crm} = detail
    const allowed = (type: ConversionCommand['type']) => detail.allowedCommands?.includes(type)
    const currency = extraPages?.currency ?? payment?.currency ?? detail.conversion.payment.currency
    const money = (minor: number | null | undefined) => formatMoney(minor, currency, i18n.language) ?? '—'
    const decisions = detail.decisions?.length ? detail.decisions : EXTRA_PAGES_DECISIONS
    const decision = extraPages?.decision ?? detail.conversion.extraPagesDecision
    const status = payment?.status ?? detail.conversion.payment.status
    const placeholder = !!extraPages?.placeholder
    const canCreate = allowed('CREATE_PAYMENT_LINK') && crm.stage === 'PAYMENT_LINK_TO_SEND'
    const canRegenerate = allowed('CREATE_PAYMENT_LINK') && crm.stage === 'AWAITING_PAYMENT'

    if (!extraPages?.extra && status === 'NONE') return null

    const createLink = (regenerate: boolean) => {
        if (busy) return
        setBusy(true)
        setPayError(null)
        sendCommand({commandId: newCommandId(), type: 'CREATE_PAYMENT_LINK', data: regenerate ? {regenerate: true} : undefined},
            regenerate ? 'pipelines.feedback.paymentLinkRegenerated' : 'pipelines.feedback.paymentLinkCreated')
            .catch((error) => setPayError(crmErrorMessage(error, t)))
            .finally(() => setBusy(false))
    }

    return (
        <section className={SECTION} aria-labelledby="pl-extra-title">
            <h3 id="pl-extra-title" className={`${SECTION_LABEL} mb-3`}>{t('pipelines.conversion.extraPages')}</h3>
            {extraPages && (
                <>
                    <p className="text-[13.5px] font-medium text-neutralBlack-900">
                        {t('pipelines.conversion.extraCalc', {pages: extraPages.pages ?? '—', included: extraPages.included ?? '—', extra: extraPages.extra ?? 0})}
                    </p>
                    <p className="mt-1 text-[20px] font-semibold tracking-[-.01em] text-neutralBlack-900">{money(extraPages.amountMinor)}</p>
                    {extraPages.breakdown?.length > 0 && (
                        <ul className="mt-2 flex flex-col gap-0.5 text-[12.5px] text-neutralBlack-700">
                            {extraPages.breakdown.map((tier) => (
                                <li key={tier.fromPage}>
                                    {t('pipelines.conversion.tier', {
                                        from: tier.fromPage, to: tier.toPage, pages: tier.pages,
                                        unit: money(tier.unitMinor), subtotal: money(tier.subtotalMinor),
                                    })}
                                </li>
                            ))}
                        </ul>
                    )}
                    {placeholder && (
                        <p role="note" className={`mt-2.5 px-3 py-2 rounded-lg text-[12.5px] leading-[1.5] ${TONE.amber}`}>
                            {t('pipelines.conversion.placeholder', {currency})}
                        </p>
                    )}
                    <div className="flex items-center gap-2.5 mt-3.5 flex-wrap">
                        <span id="pl-decision-label" className="text-[12.5px] text-neutralBlack-600">{t('pipelines.conversion.decision')}</span>
                        {allowed('SET_EXTRA_PAGES_DECISION') ? (
                            <span role="radiogroup" aria-labelledby="pl-decision-label" className="inline-flex rounded-lg border border-neutralBlack-200 overflow-hidden">
                                {decisions.map((d: ExtraPagesDecision) => {
                                    const on = (decision ?? 'NONE') === d
                                    return (
                                        <button key={d} type="button" role="radio" aria-checked={on}
                                                onClick={() => !on && sendCommand({commandId: newCommandId(), type: 'SET_EXTRA_PAGES_DECISION', data: {decision: d}},
                                                    'pipelines.feedback.decision').catch(() => undefined)}
                                                className={`h-8 px-3 text-[12.5px] font-semibold transition-colors duration-200 ease-out ${on ? 'bg-neutralBlack-900 text-white' : 'bg-white text-neutralBlack-700 hover:bg-neutralBlack-100'}`}>
                                            {t(`pipelines.decision.${d}`)}
                                        </button>
                                    )
                                })}
                            </span>
                        ) : (
                            <Tag>{t(`pipelines.decision.${decision ?? 'NONE'}`)}</Tag>
                        )}
                    </div>
                </>
            )}

            <div className="mt-4">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12.5px] text-neutralBlack-600">{t('pipelines.conversion.payment')}</span>
                    <Tag tone={status === 'PAID' ? 'green' : status === 'FAILED' ? 'red' : status === 'LINK_CREATED' ? 'amber' : 'grey'}>
                        {t(`pipelines.payment.${status}`, {amount: money(payment?.amountMinor ?? extraPages?.amountMinor)})}
                    </Tag>
                </div>
                {payment?.url && (
                    <div className="mt-2.5 flex items-center gap-2 rounded-[10px] border border-neutralBlack-200 px-3 py-2">
                        <span className="flex-1 min-w-0 truncate text-[13px] font-medium text-[#14343B]">{payment.url}</span>
                        <CopyButton text={payment.url} label={t('pipelines.conversion.copyPaymentLink')}
                                    className={`${LINK_BTN} h-7`}/>
                        <ExternalLink href={payment.url}>{t('pipelines.conversion.open')}</ExternalLink>
                    </div>
                )}
                {(payment?.linkCreatedAt || payment?.paidAt || payment?.failedAt) && (
                    <p className="mt-1.5 text-[12px] text-neutralBlack-600">
                        {payment.paidAt ? t('pipelines.conversion.paidAt', {date: formatDateTime(payment.paidAt)})
                            : payment.failedAt ? t('pipelines.conversion.failedAt', {date: formatDateTime(payment.failedAt)})
                                : t('pipelines.conversion.linkCreatedAt', {date: formatDateTime(payment.linkCreatedAt)})}
                    </p>
                )}
                {(canCreate || canRegenerate) && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={() => createLink(canRegenerate)} disabled={busy || placeholder}
                                className={canRegenerate ? GHOST_BTN : PRIMARY_BTN}>
                            {busy ? <Spinner/> : canRegenerate ? t('pipelines.conversion.regeneratePaymentLink') : t('pipelines.conversion.createPaymentLink')}
                        </button>
                        {placeholder && <span className="text-[12px] text-neutralBlack-600">{t('pipelines.conversion.placeholderShort')}</span>}
                    </div>
                )}
                {payError && <p role="alert" className="mt-2 text-[12.5px] leading-[1.5] text-[#B42318]">{payError}</p>}
            </div>
        </section>
    )
}

// ── Print and delivery ───────────────────────────────────────────────────

const PrintSection: React.FC<{
    detail: CrmConversionDetail
    sendCommand: SendCommand
    onSentToPrint: () => void
    onShipped: () => void
}> = ({detail, sendCommand, onSentToPrint, onShipped}) => {
    const {t} = useTranslation('dashboard')
    const {formatDateTime} = useDateFormatters()
    const {print, crm} = detail
    const allowed = (type: ConversionCommand['type']) => detail.allowedCommands?.includes(type)
    const can = {
        sent: allowed('MARK_SENT_TO_PRINT') && crm.stage === 'READY_TO_PRINT',
        shipped: allowed('MARK_SHIPPED') && crm.stage === 'SENT_TO_PRINT_SHOP',
        delivered: allowed('MARK_DELIVERED') && (crm.stage === 'SENT_TO_PRINT_SHOP' || crm.stage === 'PRINTING_SHIPPING'),
    }
    const hasData = !!(print?.sentToPrintAt || print?.shippedAt || print?.deliveredAt)
    if (!hasData && !PRINT_STAGES.includes(crm.stage)) return null

    return (
        <section className={SECTION} aria-labelledby="pl-print-title">
            <h3 id="pl-print-title" className={`${SECTION_LABEL} mb-3`}>{t('pipelines.conversion.print')}</h3>
            <Row label={t('pipelines.stage.SENT_TO_PRINT_SHOP')}>
                {formatDateTime(print?.sentToPrintAt)}
                {print?.printChannel && <Tag>{t(`pipelines.printChannel.${print.printChannel}`, {defaultValue: print.printChannel})}</Tag>}
            </Row>
            <Row label={t('pipelines.conversion.shipped')}>
                {formatDateTime(print?.shippedAt)}
                {print?.trackingUrl && <ExternalLink href={print.trackingUrl}>{t('pipelines.conversion.track')}</ExternalLink>}
            </Row>
            <Row label={t('pipelines.stage.DELIVERED')}>{formatDateTime(print?.deliveredAt)}</Row>
            {(can.sent || can.shipped || can.delivered) && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {can.sent && <button type="button" onClick={onSentToPrint} className={PRIMARY_BTN}>{t('pipelines.conversion.markSentToPrint')}</button>}
                    {can.shipped && <button type="button" onClick={onShipped} className={PRIMARY_BTN}>{t('pipelines.conversion.markShipped')}</button>}
                    {can.delivered && (
                        <button type="button" className={can.shipped ? GHOST_BTN : PRIMARY_BTN}
                                onClick={() => sendCommand({commandId: newCommandId(), type: 'MARK_DELIVERED'}, 'pipelines.feedback.delivered').catch(() => undefined)}>
                            {t('pipelines.conversion.markDelivered')}
                        </button>
                    )}
                </div>
            )}
        </section>
    )
}

// ── Shortcuts and ids ────────────────────────────────────────────────────

const ShortcutsSection: React.FC<{detail: CrmConversionDetail}> = ({detail}) => {
    const {t} = useTranslation('dashboard')
    const {conversion, shortcuts} = detail
    const links = [
        {url: conversion.tripId ? tripBookEditorUrl(conversion.tripId) : null, label: t('pipelines.conversion.openEditor')},
        {url: shortcuts?.flipbookUrl, label: t('pipelines.conversion.openFlipbook')},
        {url: shortcuts?.paymentUrl, label: t('pipelines.conversion.openPayment')},
        {url: shortcuts?.trackingUrl, label: t('pipelines.conversion.track')},
    ].filter((l): l is {url: string; label: string} => !!l.url)
    return (
        <section className={SECTION} aria-labelledby="pl-short-title">
            <h3 id="pl-short-title" className={`${SECTION_LABEL} mb-3`}>{t('pipelines.conversion.shortcuts')}</h3>
            {links.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-2">
                    {links.map((l) => <ExternalLink key={l.url} href={l.url}>{l.label}</ExternalLink>)}
                </div>
            )}
            {conversion.tripId && (
                <Row label={t('pipelines.panel.tripId')}>
                    <span className="tabular-nums truncate">{conversion.tripId}</span>
                    <CopyButton text={conversion.tripId} label={t('pipelines.panel.copyTripId')}/>
                </Row>
            )}
            {conversion.invitationCode && (
                <Row label={t('pipelines.panel.codeValue')}>
                    <span className="font-semibold tabular-nums tracking-[.02em]">{conversion.invitationCode}</span>
                    <CopyButton text={conversion.invitationCode} label={t('pipelines.panel.copyCode')}/>
                </Row>
            )}
        </section>
    )
}

// ── Panel ────────────────────────────────────────────────────────────────

// Side panel for one conversion (GET /crm/conversions/{id}). Every action is a
// command that answers with the fresh detail.
export const ConversionDetailPanel: React.FC<ConversionDetailPanelProps> = ({
    conversionId, team, currentUserId, onClose, onRequestMove, onRequestOverride, onRequestLost,
}) => {
    const {t} = useTranslation('dashboard')
    const {formatTripRange, formatDay} = useDateFormatters()
    const {detail, isLoadingDetail, isFetchingDetail, detailError} = useCrmConversion(conversionId)
    const {sendConversionCommand} = useCrmMutations()
    const [dialog, setDialog] = useState<null | 'done' | 'followUp' | 'draftSent' | 'hold' | 'reason' | 'print'>(null)
    const [valueRequest, setValueRequest] = useState<{kind: 'flipbook' | 'pages' | 'shipped'; initial?: string | null} | null>(null)
    const [channel, setChannel] = useState<ActivationChannel | undefined>(undefined)
    const [editContactRequest, setEditContactRequest] = useState(0)
    const titleId = 'pl-conv-panel-title'

    useEffect(() => setChannel(undefined), [conversionId])

    const sendCommand: SendCommand = (command, successKey) =>
        sendConversionCommand({id: conversionId, command})
            .then((result) => {
                enqueueSnackbar(t(successKey), {variant: 'success'})
                return result
            })
            .catch((error) => {
                enqueueSnackbar(crmErrorMessage(error, t), {variant: 'error'})
                throw error
            })

    // Dialog submits surface their own error inline, so no error toast there.
    const sendFromDialog = (command: ConversionCommand, successKey: string) =>
        sendConversionCommand({id: conversionId, command}).then(() => enqueueSnackbar(t(successKey), {variant: 'success'}))

    const conversion = detail?.conversion
    const stage = detail?.crm.stage
    const allowed = (type: ConversionCommand['type']) => !!detail?.allowedCommands?.includes(type)
    const touch = detail?.nextTouch?.touchpoint
    const defaultChannel: ActivationChannel = channel ?? detail?.nextTouch?.preparedMessage?.channel ?? touch?.channel ?? conversion?.traveler.preferredChannel ?? 'EMAIL'
    const name = conversion ? travelerName(conversion.traveler, t('pipelines.card.noName')) : ''
    const counter = conversion ? dayCounter(conversion.trip, t) : null
    const onHold = conversion?.flags.onHold && conversion.flags.onHold.until > Date.now() ? conversion.flags.onHold : null
    const isFinal = isFinalConversionStage(stage)
    const agencyLabel = detail?.agency?.name ?? conversion?.agencyName ?? (conversion ? t(`pipelines.salesChannel.${conversion.channel ?? 'B2C'}`) : '')
    const stageTargets = allowed('SET_STAGE') ? detail?.allowedStages ?? [] : []
    const canMarkDraftSent = allowed('TOUCHPOINT_DONE') && !!stage && DRAFT_SENT_STAGES.includes(stage)

    const header = detail && conversion && stage && (
        <>
            <div className="flex items-center gap-2 mt-2">
                <Avatar name={agencyLabel} size={24}/>
                <span className="text-[13px] text-neutralBlack-700">{agencyLabel}</span>
                {detail.agency?.accountManagerName && (
                    <span className="text-[12.5px] text-neutralBlack-600">· {t('pipelines.panel.accountManager')} {detail.agency.accountManagerName}</span>
                )}
            </div>
            <div className="flex items-center flex-wrap gap-1.5 mt-3">
                {conversion.mode && <Tag tone="teal">{t(`pipelines.mode.${conversion.mode}`)}</Tag>}
                <MarketChip market={conversion.market}/>
                {counter && <Tag>{counter}</Tag>}
                {conversion.channel && <Tag>{t(`pipelines.salesChannel.${conversion.channel}`)}</Tag>}
                {detail.crm.lostReason && <Tag>{t(`pipelines.lostReason.${detail.crm.lostReason}`)}</Tag>}
            </div>
            <div className="mt-4">
                <span className={`${SECTION_LABEL} block mb-1.5`}>{t('pipelines.conversion.stage')}</span>
                <div className="flex items-center gap-2 flex-wrap">
                    <Tag tone="teal">{t(`pipelines.stage.${stage}`)}</Tag>
                    {stageTargets.length > 0 && (
                        <label className="inline-flex items-center">
                            <span className="sr-only">{t('pipelines.card.moveTo')}</span>
                            <select value="" onChange={(e) => e.target.value && onRequestMove(conversionId, e.target.value as ConversionStage, stage)}
                                    className="h-8 pl-2.5 pr-7 rounded-lg border border-neutralBlack-200 bg-white text-[12.5px] font-medium text-neutralBlack-900">
                                <option value="">{t('pipelines.card.moveTo')}</option>
                                {stageTargets.map((s) => <option key={s} value={s}>{t(`pipelines.stage.${s}`)}</option>)}
                            </select>
                        </label>
                    )}
                    {allowed('APPROVE_DRAFT') && (
                        <button type="button" className={GHOST_BTN}
                                onClick={() => sendCommand({commandId: newCommandId(), type: 'APPROVE_DRAFT'}, 'pipelines.feedback.draftApproved').catch(() => undefined)}>
                            {t('pipelines.conversion.approveDraft')}
                        </button>
                    )}
                </div>
            </div>
            <div className="mt-2.5">
                <ConversionBadges onHold={conversion.flags.onHold} agencyReview={conversion.flags.agencyReview}
                                  alerts={detail.crm.alerts} messagesSent={detail.crm.messagesSent}/>
            </div>
            {detail.crm.override && (
                <p className="mt-2 text-[12.5px] text-neutralBlack-700">{t('pipelines.conversion.overridden', {note: detail.crm.override.note})}</p>
            )}
            {detail.crm.lostNote && <p className="mt-2 text-[12.5px] text-neutralBlack-700">{detail.crm.lostNote}</p>}
        </>
    )

    const footer = detail && stage && (
        <>
            {onHold && allowed('RESUME') && (
                <button type="button" className={FOOTER_BTN}
                        onClick={() => sendCommand({commandId: newCommandId(), type: 'RESUME'}, 'pipelines.feedback.resumed').catch(() => undefined)}>
                    {t('pipelines.conversion.resume')}
                </button>
            )}
            {!onHold && allowed('HOLD') && (
                <button type="button" className={FOOTER_BTN} onClick={() => setDialog('hold')}>{t('pipelines.conversion.putOnHold')}</button>
            )}
            {allowed('OVERRIDE_STAGE') && (
                <button type="button" className={FOOTER_BTN} onClick={() => onRequestOverride(conversionId, stage)}>{t('pipelines.card.override')}</button>
            )}
            {!isFinal && allowed('MARK_LOST') && (
                <button type="button" className={FOOTER_BTN} onClick={() => onRequestLost(conversionId, null, detail.lostReasons)}>{t('pipelines.panel.markLost')}</button>
            )}
        </>
    )

    return (
        <>
            <DetailPanelShell
                entityKey={conversionId} titleId={titleId} width={520} isRefreshing={isFetchingDetail && !isLoadingDetail} onClose={onClose}
                topLine={conversion && (
                    <>
                        <Countries countries={conversion.trip.countries}/>
                        <span className="ml-1 text-[15px] font-bold tracking-[-.01em] text-neutralBlack-900 whitespace-nowrap">
                            {formatTripRange(conversion.trip.startDate, conversion.trip.endDate)}
                        </span>
                    </>
                )}
                title={isLoadingDetail ? t('pipelines.loading') : name || t('pipelines.panel.title')}
                header={header}
                footer={footer}
            >
                {isLoadingDetail ? (
                    <div className="px-6 py-5 flex flex-col gap-3">
                        {Array.from({length: 4}).map((_, i) => <div key={i} className="h-20 rounded-xl bg-neutralBlack-50 animate-pulse"/>)}
                    </div>
                ) : detailError && !detail ? (
                    <div role="alert" className="px-6 py-10 text-center text-[13px] text-neutralBlack-700">{crmErrorMessage(detailError, t)}</div>
                ) : detail && conversion && stage ? (
                    <>
                        {onHold && (
                            <div role="status" className="flex items-center gap-2.5 px-6 py-3 bg-neutralBlack-50 border-b border-neutralBlack-100 text-[13px] text-neutralBlack-800">
                                <span className="font-semibold">{t('pipelines.badge.onHoldUntil', {date: formatDay(onHold.until)})}</span>
                                {onHold.note && <span className="text-neutralBlack-600 truncate">· {onHold.note}</span>}
                            </div>
                        )}
                        {stage === 'DETAILS_PENDING' && (
                            <div className="px-6 py-3.5 bg-[#FFF4D6] border-b border-[#F3E2B0]">
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <span className="text-[13px] font-semibold text-[#8A5A00]">{t('pipelines.stage.DETAILS_PENDING')}</span>
                                    {(detail.tripBook?.pendingReason ?? conversion.pendingReason) && (
                                        <Tag tone="amber">{t(`pipelines.pendingReason.${detail.tripBook?.pendingReason ?? conversion.pendingReason}`)}</Tag>
                                    )}
                                    <span className="flex-1"/>
                                    {allowed('SET_PENDING_REASON') && (
                                        <button type="button" onClick={() => setDialog('reason')}
                                                className="h-8 px-3 rounded-lg border border-[#E8D49A] bg-white text-[12.5px] font-semibold text-[#8A5A00] hover:bg-[#FFF9E8] transition-colors duration-200 ease-out">
                                            {t('pipelines.pending.change')}
                                        </button>
                                    )}
                                </div>
                                {(detail.tripBook?.pendingReason ?? conversion.pendingReason) && (
                                    <p className="mt-1.5 text-[12.5px] leading-[1.5] text-[#6B5200]">
                                        {t(`pipelines.pendingReasonHelp.${detail.tripBook?.pendingReason ?? conversion.pendingReason}`)}
                                    </p>
                                )}
                            </div>
                        )}
                        <NextTouchSection
                            detail={{crm: detail.crm, nextTouch: detail.nextTouch, allowedCommands: detail.allowedCommands ?? []}}
                            team={team} channel={channel} onChannelChange={setChannel}
                            onMarkDone={() => setDialog('done')} onAddFollowUp={() => setDialog('followUp')}
                            onEditContact={() => setEditContactRequest((n) => n + 1)}
                            sendCommand={sendCommand} isFetching={isFetchingDetail}
                            waitDateLabel={(date) => t('pipelines.panel.until', {date})}
                            extraActions={canMarkDraftSent && (
                                <button type="button" onClick={() => setDialog('draftSent')} className={PRIMARY_BTN}>{t('pipelines.conversion.markDraftSent')}</button>
                            )}/>
                        <TripBookSection detail={detail} team={team} sendCommand={sendCommand}
                                         onSetPages={() => setValueRequest({kind: 'pages', initial: String(detail.tripBook?.pagesUploaded ?? conversion.pages.uploaded ?? '')})}
                                         onSetFlipbook={() => setValueRequest({kind: 'flipbook', initial: detail.tripBook?.flipbookUrl ?? null})}/>
                        <ExtraPagesSection detail={detail} sendCommand={sendCommand}/>
                        <PrintSection detail={detail} sendCommand={sendCommand}
                                      onSentToPrint={() => setDialog('print')} onShipped={() => setValueRequest({kind: 'shipped'})}/>
                        <ContactSection traveler={conversion.traveler} canEdit={allowed('UPDATE_CONTACT')} editRequest={editContactRequest}
                                        sendCommand={sendCommand} title={t('pipelines.conversion.customer')}/>
                        <ShortcutsSection detail={detail}/>
                        <NotesSection pipeline="conversion" id={conversionId} canAdd={detail.allowedCommands?.includes('ADD_NOTE') ?? true}/>
                        <HistorySection pipeline="conversion" id={conversionId} history={detail.history} team={team}/>
                    </>
                ) : null}
            </DetailPanelShell>

            {detail && (
                <>
                    <MarkDoneDialog isOpen={dialog === 'done'} defaultChannel={defaultChannel} outcomes={detail.outcomes} deadline={null}
                                    helpKeyPrefix="pipelines.conversionOutcomeHelp"
                                    onClose={() => setDialog(null)}
                                    onSubmit={({outcome, channel: usedChannel, note}) => sendFromDialog({
                                        commandId: newCommandId(), type: 'TOUCHPOINT_DONE', note: note || undefined,
                                        data: {touchpointId: touch?.id, channel: usedChannel, outcome: outcome as ConversionOutcome},
                                    }, 'pipelines.feedback.done')}/>
                    <AddFollowUpDialog isOpen={dialog === 'followUp'} defaultChannel={defaultChannel} team={team} currentUserId={currentUserId}
                                       deadline={null} onClose={() => setDialog(null)}
                                       onSubmit={({date, channel: fuChannel, assignee, note}) => sendFromDialog({
                                           commandId: newCommandId(), type: 'ADD_FOLLOW_UP', note: note || undefined,
                                           data: {date, channel: fuChannel, assignee},
                                       }, 'pipelines.feedback.followUpAdded')}/>
                    <DraftSentDialog isOpen={dialog === 'draftSent'} defaultChannel={defaultChannel}
                                     defaultPages={detail.tripBook?.pagesUploaded ?? conversion?.pages.uploaded ?? null}
                                     onClose={() => setDialog(null)}
                                     onSubmit={({channel: usedChannel, pages, note}) => sendFromDialog({
                                         commandId: newCommandId(), type: 'TOUCHPOINT_DONE', note: note || undefined,
                                         data: {channel: usedChannel, outcome: 'SENT', pages},
                                     }, 'pipelines.feedback.draftSent')}/>
                    <HoldDialog isOpen={dialog === 'hold'} onClose={() => setDialog(null)}
                                onSubmit={({date, note}) => sendFromDialog({
                                    commandId: newCommandId(), type: 'HOLD', note: note || undefined, data: {date, note: note || undefined},
                                }, 'pipelines.feedback.onHold')}/>
                    <PendingReasonDialog isOpen={dialog === 'reason'} title={t('pipelines.pending.changeTitle')} submitLabel={t('pipelines.save')}
                                         reasons={detail.pendingReasons} initial={detail.tripBook?.pendingReason ?? conversion?.pendingReason ?? null}
                                         onClose={() => setDialog(null)}
                                         onSubmit={(reason, note) => sendFromDialog({
                                             commandId: newCommandId(), type: 'SET_PENDING_REASON', note: note || undefined, data: {reason},
                                         }, 'pipelines.feedback.reasonChanged')}/>
                    <SentToPrintDialog isOpen={dialog === 'print'} initial={detail.tripBook?.printChannel ?? detail.print?.printChannel ?? null}
                                       onClose={() => setDialog(null)}
                                       onSubmit={(printChannel) => sendFromDialog({
                                           commandId: newCommandId(), type: 'MARK_SENT_TO_PRINT', data: printChannel ? {printChannel} : undefined,
                                       }, 'pipelines.feedback.sentToPrint')}/>
                    <ValueDialog request={valueRequest} onClose={() => setValueRequest(null)}
                                 onSubmit={(value) => {
                                     const kind = valueRequest?.kind
                                     if (kind === 'pages') return sendFromDialog({commandId: newCommandId(), type: 'SET_PAGES', data: {pages: Number(value)}}, 'pipelines.feedback.pagesSet')
                                     if (kind === 'flipbook') return sendFromDialog({commandId: newCommandId(), type: 'SET_FLIPBOOK', data: {url: value}}, 'pipelines.feedback.flipbookSet')
                                     return sendFromDialog({commandId: newCommandId(), type: 'MARK_SHIPPED', data: value ? {trackingUrl: value} : undefined}, 'pipelines.feedback.shipped')
                                 }}/>
                </>
            )}
        </>
    )
}
